import _ from 'lodash'
import httpClient from '../../api/httpClient'
import { makeClient, serializeClient } from '../../lib/types/Client'
import { RevenueTier } from '../../lib/types/RevenueTier'
import { fetchWrapper } from '../../utils/helper'

const emptyRevenueTier = (id) => RevenueTier(id, -1, '', '', '', false, false)

const clientInitialValue = {
  id: null,
  name: '',
  config: '',
  feed: '',
  locale: '',
  ownPaymentSystem: false,
  revenue: {
    share: '',
    tiers: [emptyRevenueTier(0)],
  },
  storeGroup: '',
  created: '',
  updated: '',
  defaultVerifiedUsers: false,
  userMailConfig: {},
  paymentOrgId: null,
  paymentOrgName: null,
  paymentOrgEmail: null,
}

export default {
  state: {
    selectedClient: { ...clientInitialValue },
    loading: false,
    error: '',
    success: '',
    saving: false,
    saveSuccess: '',
    updating: false,
    updateSuccess: '',
    userMailTemplates: [],
    userActions: [],
    paymentOrganizations: [],
    showPaymentOrgForm: false,
    addingNewPaymentOrg: false,
    addingPaymentOrgSuccess: '',
    addingPaymentOrgError: '',
  },

  mutations: {
    setStatus: (state, status) => (state.status = status),

    resetClient: (state) => (state.selectedClient = { ...clientInitialValue }),

    update(state, { path, value }) {
      // Use lodash to set nested object value
      _.set(state, path, value)
    },

    addTier(state) {
      state.selectedClient.revenue = {
        ...state.selectedClient.revenue,
        tiers: state.selectedClient.revenue.tiers.concat(emptyRevenueTier(state.selectedClient.revenue.tiers.length)),
      }
    },

    removeTier(state, id) {
      state.selectedClient.revenue = {
        ...state.selectedClient.revenue,
        tiers: state.selectedClient.revenue.tiers.filter((tier) => tier.id !== id),
      }
    },
  },

  getters: {
    selectedClient: (state) => state.selectedClient,
    clientLoading: (state) => state.loading,
    clientError: (state) => state.error,
    clientSuccess: (state) => state.success,
    saving: (state) => state.saving,
    saveSuccess: (state) => state.saveSuccess,
    updating: (state) => state.updating,
    updateSuccess: (state) => state.updateSuccess,
    userMailTemplates: (state) => state.userMailTemplates,
    userActions: (state) => state.userActions,
    paymentOrganizations: (state) => state.paymentOrganizations,
    showPaymentOrgForm: (state) => state.showPaymentOrgForm,
    get: (state) => (path) => state[path],
  },

  actions: {
    async fetchClient({ commit }, id) {
      const response = await httpClient.get(`/clients/${id}`)
      const client = makeClient(response.data)
      client.revenue.tiers.forEach((el) => {
        el.instagram_share = (Number(el.instagram_share).toFixed(2) * 100).toFixed(0)
        el.share = (Number(el.share).toFixed(2) * 100).toFixed(0)
      })
      commit('update', { path: 'selectedClient', value: client })
    },
    createClient({ commit, state }) {
      fetchWrapper(
        async (client) => {
          return httpClient.post(`/clients`, serializeClient(client))
        },
        commit,
        [state.selectedClient],
        {
          commit,
          loadingPath: 'updating',
          successPath: 'saveSuccess',
          successMessage: 'Client was successfully created!',
        },
      )
    },

    updateClient({ commit, state }) {
      fetchWrapper(
        async (client) => {
          return httpClient.put(`/clients/${client.id}`, serializeClient(client))
        },
        commit,
        [state.selectedClient],
        {
          loadingPath: 'saving',
          successPath: 'updateSuccess',
          successMessage: 'Client was successfully updated!',
        },
      )
    },

    async fetchUserMailConfig({ commit }) {
      const { data } = await httpClient.get(`clients/user-mail-config`)
      commit('update', { path: 'userActions', value: data.actions })
      if (Array.isArray(data.templates)) {
        commit('update', { path: 'userMailTemplates', value: data.templates })
      }
    },

    async fetchPaymentOrganizations({ commit }) {
      const { data } = await httpClient.get('clients/payment-organizations')
      const paymentOrgs = data.map((item) => ({
        label: item.name,
        value: item.id,
      }))
      commit('update', { path: 'paymentOrganizations', value: paymentOrgs })
    },
    addNewPaymentOrganization({ commit, state }) {
      fetchWrapper(
        async () => {
          const { data } = await httpClient.post('clients/payment-organizations', {
            name: state.selectedClient.paymentOrgName,
            email: state.selectedClient.paymentOrgEmail,
          })
          commit('update', {
            path: 'paymentOrganizations',
            value: [...state.paymentOrganizations, { label: data.name, value: data.id }],
          })

          commit('update', {
            path: 'selectedClient.paymentOrgId',
            value: data.id,
          })
          commit('update', {
            path: 'showPaymentOrgForm',
            value: false,
          })
        },
        commit,
        [],
        {
          loadingPath: 'addingNewPaymentOrg',
          successPath: 'addingPaymentOrgSuccess',
          errorPath: 'addingPaymentOrgError',
          successMessage: 'Payment organization has been successfully added!',
        },
      )
    },

    async initClient({ commit, dispatch }, id = null) {
      commit('resetClient')
      if (id) {
        return fetchWrapper(
          async (dispatch, id) => {
            await dispatch('fetchClient', id)
            dispatch('storeGroups/fetch', {}, { root: true })
            dispatch('fetchUserMailConfig')
            dispatch('fetchPaymentOrganizations')
          },
          commit,
          [dispatch, id],
          { successMessage: 'Client has been successfully fetched!' },
        )
      }
      return fetchWrapper(
        async (dispatch) => {
          dispatch('storeGroups/fetch', {}, { root: true })
          dispatch('fetchUserMailConfig')
          dispatch('fetchPaymentOrganizations')
        },
        commit,
        [dispatch],
        { successMessage: 'Client has been successfully initialized' },
      )
    },

    setUserMailTemplate({ commit, state }, { key, value }) {
      commit('update', {
        path: 'selectedClient.userMailConfig',
        value: {
          ...state.selectedClient.userMailConfig,
          [key]: value,
        },
      })
    },
  },
}
